// src/shared/utils/download.ts

import type { BlobResponse } from '@/shared/api/WebApi';

/**
 * Parse the filename from a Content-Disposition header value.
 * Handles `attachment; filename="report.csv"` (quoted) and
 * `attachment; filename=report.csv` (unquoted). Returns null when the header
 * is absent, has no filename parameter, or the parameter is empty/malformed —
 * callers fall back to a static name.
 */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\s*=\s*(?:"([^"]*)"|([^;\s]+))/i.exec(header);
  if (!match) return null;
  const filename = (match[1] ?? match[2] ?? '').trim();
  return filename.length > 0 ? filename : null;
}

/**
 * Trigger a browser download for an authenticated blob response
 * (object URL + anchor click + revoke). Filename comes from the response's
 * Content-Disposition when present, else the caller's fallback.
 */
export function triggerBlobDownload(res: BlobResponse, fallbackFilename: string): void {
  const url = URL.createObjectURL(res.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = res.filename ?? fallbackFilename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
